import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { formatDate } from "../lib/format";

// A minimal, printer-friendly single-page invoice/order slip.
// Auto-triggers window.print() once loaded.
export default function OrderPrint() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const o = await api.get(`/orders/${id}`);
        setOrder(o.data);
        if (o.data.party_id) {
          try {
            const p = await api.get(`/parties/${o.data.party_id}/detail`);
            setBuyer(p.data.party);
          } catch (err) {
            // Non-critical: printable page still works without the buyer's address/phone.
            console.debug("Buyer detail lookup failed:", err?.message);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!loading && order) {
      // slight delay so the DOM paints before the print dialog
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [loading, order]);

  if (loading) return <div style={{ padding: 24, fontFamily: "sans-serif" }}>Loading…</div>;
  if (!order)  return <div style={{ padding: 24, fontFamily: "sans-serif" }}>Order not found.</div>;

  const items = order.items || [];
  const totalQty = items.reduce((n, it) => n + Number(it.quantity || 0), 0);
  const shortId = order.id.slice(0, 8).toUpperCase();

  return (
    <div className="print-sheet">
      <style>{`
        @page { size: A4; margin: 16mm; }
        html, body { background: #fff; margin: 0; }
        .print-sheet {
          font-family: "Helvetica Neue", Arial, sans-serif;
          color: #222;
          max-width: 780px;
          margin: 0 auto;
          padding: 28px;
          background: #fff;
        }
        .p-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 3px solid #224A38;
          padding-bottom: 14px;
          margin-bottom: 22px;
        }
        .p-brand-title { font-size: 26px; font-weight: 800; color: #224A38; letter-spacing: 0.3px; }
        .p-brand-sub   { font-size: 12px; color: #555; margin-top: 4px; }
        .p-order-meta  { text-align: right; font-size: 12px; color: #333; }
        .p-order-meta .num { font-size: 18px; font-weight: 700; color: #222; }
        .p-box {
          border: 1px solid #ddd;
          border-radius: 6px;
          padding: 12px 14px;
          margin-bottom: 16px;
        }
        .p-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .p-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.6px; color: #666; margin-bottom: 4px; }
        .p-value { font-size: 14px; color: #111; font-weight: 500; }
        table.p-items { width: 100%; border-collapse: collapse; margin-top: 6px; }
        table.p-items th, table.p-items td {
          border: 1px solid #ddd;
          padding: 8px 10px;
          text-align: left;
          font-size: 13px;
        }
        table.p-items th { background: #f4f2ec; font-weight: 700; }
        table.p-items td.qty { text-align: right; font-variant-numeric: tabular-nums; }
        table.p-items tfoot td { font-weight: 700; background: #fafaf7; }
        .p-notes { font-size: 12px; color: #333; margin-top: 14px; white-space: pre-wrap; }
        .p-sign {
          margin-top: 40px;
          display: flex;
          justify-content: space-between;
          gap: 40px;
        }
        .p-sign > div {
          flex: 1;
          border-top: 1px solid #333;
          padding-top: 6px;
          font-size: 12px;
          color: #444;
          text-align: center;
        }
        .p-footer {
          margin-top: 30px;
          text-align: center;
          color: #888;
          font-size: 11px;
        }
        .no-print { margin: 20px; text-align: center; }
        @media print {
          .no-print { display: none; }
        }
        .p-badge {
          display: inline-block;
          font-size: 11px;
          padding: 3px 8px;
          border-radius: 999px;
          background: #eef4ee;
          color: #224A38;
          border: 1px solid #d4e0d5;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }
      `}</style>

      {/* Header */}
      <div className="p-header">
        <div>
          <div className="p-brand-title">Hygenix Healthcare</div>
          <div className="p-brand-sub">Order &amp; Dispatch Slip</div>
        </div>
        <div className="p-order-meta">
          <div>Order</div>
          <div className="num">#{shortId}</div>
          <div style={{ marginTop: 4 }}>Received: {formatDate(order.received_at)}</div>
          <div>Dispatch By: {order.dispatch_by ? formatDate(order.dispatch_by) : "—"}</div>
          <div style={{ marginTop: 6 }}>
            <span className="p-badge">{(order.status || "pending").replace("_", " ")}</span>
          </div>
        </div>
      </div>

      {/* Buyer + Employee */}
      <div className="p-box">
        <div className="p-grid">
          <div>
            <div className="p-label">Buyer</div>
            <div className="p-value">{order.party_name}</div>
            {buyer?.phone && <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>Phone: {buyer.phone}</div>}
            {buyer?.address && <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{buyer.address}</div>}
          </div>
          <div>
            <div className="p-label">Assigned Employee</div>
            <div className="p-value">{order.employee_name || "—"}</div>
          </div>
        </div>
      </div>

      {/* Items */}
      <table className="p-items">
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Brand</th>
            <th style={{ width: 90 }}>Size</th>
            <th style={{ width: 110 }} className="qty">Quantity</th>
            <th style={{ width: 90 }}>Unit</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={5} style={{ textAlign: "center", color: "#888" }}>No line items</td></tr>
          ) : items.map((it, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{it.brand}</td>
              <td>{it.size}</td>
              <td className="qty">{it.quantity}</td>
              <td>{it.unit || "cartons"}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ textAlign: "right" }}>Total Quantity</td>
            <td className="qty">{totalQty}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>

      {order.notes && (
        <div className="p-notes">
          <div className="p-label" style={{ marginTop: 14 }}>Notes</div>
          {order.notes}
        </div>
      )}

      {/* Signatures */}
      <div className="p-sign">
        <div>Prepared By</div>
        <div>Dispatched By</div>
        <div>Received By (Buyer)</div>
      </div>

      <div className="p-footer">
        Hygenix Healthcare · Generated on {formatDate(new Date().toISOString())}
      </div>

      <div className="no-print">
        <button
          onClick={() => window.print()}
          style={{
            background: "#224A38", color: "#fff", border: 0, padding: "10px 20px",
            borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600,
          }}
        >Print again</button>
        <button
          onClick={() => window.close()}
          style={{
            marginLeft: 10, background: "#eee", color: "#222", border: 0, padding: "10px 20px",
            borderRadius: 8, cursor: "pointer", fontSize: 14,
          }}
        >Close</button>
      </div>
    </div>
  );
}
